import React from 'react'
import { Badge, Button, Table, Td, Th, Tr } from '@/shared/ui'
import { formatMoneyFromCents } from '@/shared/i18n/money'
import { createRevsharePayout, cancelRevsharePayout, finalizeRevsharePayout } from '../services/revsharePayoutService'
import { getRevshareUserMonthlySummary, type RevshareMonthlySummaryRow } from '../services/revshareSummaryService'
import { bpsToPercent } from '../utils/percent'
import { monthStartDateToLabel } from '../utils/month'

type Props = {
  userId: string
  refreshKey: number
  onPayoutChanged?: () => void
}

function iso(v?: string | null) {
  return v ? new Date(v).toLocaleString() : '-'
}

export default function RevshareMonthlySummaryPanel({ userId, refreshKey, onPayoutChanged }: Props) {
  const [rows, setRows] = React.useState<RevshareMonthlySummaryRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [actionId, setActionId] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await getRevshareUserMonthlySummary(userId, 12))
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar resumo mensal')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [userId])

  React.useEffect(() => { load() }, [load, refreshKey])

  async function handleCreatePayout(row: RevshareMonthlySummaryRow) {
    if (!confirm(`Criar payout de ${monthStartDateToLabel(row.competence_month)}?`)) return
    setActionId(row.competence_month)
    setError(null)
    try {
      await createRevsharePayout({ user_id: userId, competence_month: row.competence_month })
      await load()
      onPayoutChanged?.()
    } catch (e: any) {
      setError(e?.message || 'Falha ao criar payout')
    } finally {
      setActionId(null)
    }
  }

  async function handleFinalizePayout(payoutId: string) {
    if (!confirm('Marcar payout como pago?')) return
    setActionId(payoutId)
    setError(null)
    try {
      await finalizeRevsharePayout(payoutId)
      await load()
      onPayoutChanged?.()
    } catch (e: any) {
      setError(e?.message || 'Falha ao marcar como pago')
    } finally {
      setActionId(null)
    }
  }

  async function handleCancelPayout(payoutId: string) {
    if (!confirm('Cancelar este payout?')) return
    setActionId(payoutId)
    setError(null)
    try {
      await cancelRevsharePayout(payoutId)
      await load()
      onPayoutChanged?.()
    } catch (e: any) {
      setError(e?.message || 'Falha ao cancelar payout')
    } finally {
      setActionId(null)
    }
  }

  return (
    <div className="mt-8 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-base font-medium">Resumo mensal (payout do mes anterior)</div>
        <Button variant="outline" disabled={loading} onClick={load}>
          Atualizar
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      <Table>
        <thead>
          <Tr>
            <Th>Mes</Th>
            <Th>Cap</Th>
            <Th>Share</Th>
            <Th>Total</Th>
            <Th>Pendente</Th>
            <Th>Disponivel</Th>
            <Th>Reservado</Th>
            <Th>Pago</Th>
            <Th>Payout</Th>
            <Th className="w-64 text-right">Acoes</Th>
          </Tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const canCreate = Number(r.available_cents || 0) > 0 && !r.payout_id
            const payoutStatus = r.payout_status || null
            const payoutId = r.payout_id || null
            const canFinalize = !!payoutId && payoutStatus && payoutStatus !== 'paid' && payoutStatus !== 'canceled'
            const canCancel = !!payoutId && payoutStatus && payoutStatus !== 'paid' && payoutStatus !== 'canceled'
            const busy = actionId === r.competence_month || actionId === payoutId
            return (
              <Tr key={r.competence_month}>
                <Td>{monthStartDateToLabel(r.competence_month)}</Td>
                <Td className="font-mono">{bpsToPercent(r.pool_cap_bps, 2)}</Td>
                <Td className="font-mono">{bpsToPercent(r.user_share_bps, 2)}</Td>
                <Td className="font-mono">{formatMoneyFromCents(r.total_cents, 'BRL')}</Td>
                <Td className="font-mono">{formatMoneyFromCents(r.pending_cents, 'BRL')}</Td>
                <Td className="font-mono">{formatMoneyFromCents(r.available_cents, 'BRL')}</Td>
                <Td className="font-mono">{formatMoneyFromCents(r.reserved_cents, 'BRL')}</Td>
                <Td className="font-mono">{formatMoneyFromCents(r.paid_cents, 'BRL')}</Td>
                <Td>
                  {payoutStatus ? (
                    <div className="flex flex-col gap-1">
                      <Badge variant="soft" className="capitalize">
                        {payoutStatus}
                      </Badge>
                      {r.payout_scheduled_for && (
                        <span className="text-xs text-foreground/60">Agendado: {iso(r.payout_scheduled_for)}</span>
                      )}
                      {r.payout_paid_at && (
                        <span className="text-xs text-foreground/60">Pago: {iso(r.payout_paid_at)}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-foreground/60">-</span>
                  )}
                </Td>
                <Td className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {canCreate && (
                      <Button className="h-8 px-2 text-xs" disabled={busy} onClick={() => handleCreatePayout(r)}>
                        Criar payout
                      </Button>
                    )}
                    {canFinalize && payoutId && (
                      <Button className="h-8 px-2 text-xs" disabled={busy} onClick={() => handleFinalizePayout(payoutId)}>
                        Marcar pago
                      </Button>
                    )}
                    {canCancel && payoutId && (
                      <Button variant="outline" className="h-8 px-2 text-xs" disabled={busy} onClick={() => handleCancelPayout(payoutId)}>
                        Cancelar
                      </Button>
                    )}
                    {!canCreate && !canFinalize && !canCancel && <span className="text-xs text-foreground/60">-</span>}
                  </div>
                </Td>
              </Tr>
            )
          })}
          {rows.length === 0 && (
            <Tr>
              <Td colSpan={10} className="py-6 text-center text-sm text-foreground/70">
                Sem dados.
              </Td>
            </Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}

